<template>
  <div ref="$el" class="vz-direction-calendar-time" :class="{'vz-direction-calendar-time-disabled': disabled}">
    <div class="text-subtitle">
      Время
    </div>
    <div class="vz-direction-calendar-time-controls-wrapper">
      <div class="vz-direction-calendar-time-controls">
        <span class="text-body">с</span>
        <vz-select
          big
          icon-right
          ref="fromTimeSelect"
          class="small-width ml-10 mb-10"
          :modelValue="fromTime"
          :icon-size="12"
          :items="fromTimeItems"
          :disabled="disabled"
          :manualScrollItem="emptyFromScrollItem"
          :rules="[{...formRules.required, customMessage: 'Заполните'}]"
          :option-second-type-item="getActualityItemTime(fromTime, fromTimeItems)?.secondType"
          list-class="vz-direction-calendar-time-controls-select-list"
          @change="onChangeTimeFrom"
        />
        <span class="text-body ml-10">до</span>
        <vz-select
          big
          icon-right
          ref="toTimeSelect"
          class="small-width ml-10 mb-10"
          :modelValue="toTime"
          :icon-size="12"
          :items="toTimeItems"
          :disabled="disabled || isFromTimeEmpty"
          :manualScrollItem="emptyToScrollItem"
          :rules="[{...formRules.required, customMessage: 'Заполните'}]"
          :option-second-type-item="getActualityItemTime(toTime, toTimeItems)?.secondType"
          list-class="vz-direction-calendar-time-controls-select-list"
          @change="onChangeTimeTo"
        />
      </div>
      <vz-checkbox
        v-if="hasFixedTime"
        label="Фиксированное время"
        class="my-10"
        :small="false"
        :modelValue="fixedTime"
        :disabled="!hasFixedPeriod || disabled"
        :class="{'opacity-1': hasFixedPeriod, 'opacity-0': !hasFixedPeriod}"
        @change="onChangeFixedTime"
      >
        <template #label>
          <div class="vz-checkbox-label vz-direction-calendar-time-fixed-time--label">
            Фиксированное <br> время
            <vz-icon
              url="/svg/icon/fixed.svg"
              :size="11"
              class="ml-5"
              no-masked-icon
            />
          </div>
        </template>
      </vz-checkbox>
    </div>
    <slot name="note"/>
    <transition name="fadedialog">
      <div v-if="fromTimeSelect?.selectedItem?.note || noteFromPeriod" class="text-caption color-primary mt-5">
        {{ fromTimeSelect?.selectedItem?.note || noteFromPeriod }}
      </div>
    </transition>
    <div v-if="hasWaiting">
      <div class="text-caption color-light mt-10">
        Водитель может приехать в любой час в рамках заданного времени. Будьте внимательны при выборе диапазона
      </div>
      <vz-direction-calendar-time-unboxing v-if="hasUnboxing" class="my-20"/>
      <div class="vz-direction-calendar-time-awaiting">
        <div class="vz-direction-calendar-time-awaiting-labels">
          <div class="text-subtitle color-dark">
            {{ waitingTimeList.label }}
          </div>
          <div class="text-caption color-light">
            {{ waitingTimeList.description }}
          </div>
        </div>
        <div class="vz-direction-calendar-time-awaiting-selector">
          <vz-select
            v-model="awaitingTime"
            :items="awaitingItemList"
            big
            class="medium-width mb-0 ml-5"
            :disabled="disabled"
            @change="onChangeAwaitingTime"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {storeToRefs} from 'pinia';
import {useOrderStore} from '~/stores/order';
import {useCityStore} from '~/stores/city';
import {useLayoutStore} from '~/stores/personal/layout';
import {OrderDirectionCalendar, SelectItem, TimetableDate, TimetableDatePeriod, TimetableTime} from '~/types/types';
import {formatDate} from '~/common/date';
import {useDayjs} from '#dayjs'
import {deepCopyObject, objTime} from '~/common/functions';
import formRules from '~/common/vz-form-rules';
import {PropType} from 'vue';
import {Dayjs} from 'dayjs';
import VzSelect from '~/components/ui-kit/vz-select.vue';
import VzDirectionCalendarTimeUnboxing
  from '~/components/personal/order/direction/calendar/vz-direction-calendar-time-unboxing.vue';

const props = defineProps({
  date: {
    type: Object as PropType<TimetableDate>,
    default: () => null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  directionTo: {
    type: Boolean,
    default: false
  },
  hasFixedTime: {
    type: Boolean,
    default: true
  },
  hasWaiting: {
    type: Boolean,
    default: true
  },
  confirmableTime: {
    type: Boolean,
    default: true
  },
})
const emit = defineEmits(['select-time'])

const dayjs = useDayjs()
const orderStore = useOrderStore()
const {isEditOrder} = storeToRefs(useLayoutStore())
const {
  waitingTimeList,
  orderForm,
  orderView,
  orderDates,
  dispatchLocation,
  destinationLocation
} = storeToRefs(useOrderStore())
const {locationByGuid} = useCityStore()
const {$showToast} = useNuxtApp()
const $el = ref<HTMLDivElement>()

const time = {
  from: '',
  to: ''
}

const hasFixedPeriod = ref(false)
const timeIntervals = ref<TimetableTime[]>([])
const hourInterval = ref(0)
const selectedTime = ref<TimetableTime>({
  type: 'day',
  value: '',
  title: '',
  minutes: 0,
  url: ''
})

// флаги для управлением функционалом пустого времени
const userChangedFromTime = ref(false)
const userChangedToTime = ref(false)

const fromTimeSelect = ref()
const toTimeSelect = ref()

const directionName = computed(() => {
  return props.directionTo ? 'destination' : 'dispatch'
})

const calendar = computed((): OrderDirectionCalendar => {
  return props.directionTo
    ? orderForm.value.directions.destination.calendar
    : orderForm.value.directions.dispatch.calendar
})

const isConfirmableTimeDispatch = computed(() => {
  return !!Number(dispatchLocation.value?.confirmable_time)
})

const isConfirmableTimeDestination = computed(() => {
  return !!Number(destinationLocation.value?.confirmable_time)
})

const hasUnboxing = computed(() => {
  return props.directionTo
    && orderForm.value.directions.destination.direction === 'address'
    && isEditOrder.value
    && !!orderForm.value.meta?.unboxingOnDelivery
})

const awaitingTime = computed({
  get(): number {
    return orderForm.value.directions[directionName.value].waitingTime
  },
  set(value: number): void {
    orderForm.value.directions[directionName.value].waitingTime = value
  }
})

const awaitingItemList = computed((): SelectItem[] => {
  const items: SelectItem[] = []
  Object.keys(waitingTimeList.value?.list || {}).forEach((k) => {
    items.push({
      title: waitingTimeList.value.list[k],
      value: parseInt(k, 10)
    })
  })
  return items
})

const fixedTime = computed((): boolean => {
  return calendar.value.fixedTime
})

const getActualityItemTime = computed(() => {
  return (time: string, items: TimetableTime[]) => {
    return items.find((item) => item.value === time)
  }
})

const fromTime = computed((): string => {
  return calendar.value.from
})

const toTime = computed((): string => {
  return calendar.value.to
})

const fixedPeriod = computed(() => {
  return props.date.periods.find(p => p.type === 'fixed')
})

const fixedPeriodFrom = computed(() => {
  return dayjs(fixedPeriod.value?.from).format('HH:mm')
})

const timeLimit = computed(() => {
  return props.date?.timeLimit
})

const fixedBeforeToTimes = computed(() => {
  const times: string[] = []

  if (fixedPeriod.value) {
    let from = dayjs(fixedPeriod.value?.from)
    const to = fromTimeWithHourInterval.value
      ? dayjs(`${props.date.date}T${fromTimeWithHourInterval.value}`)
      : ''

    const stepMinute = fixedPeriod.value.stepMinute

    if (stepMinute) {
      while (from.isBefore(dayjs(to))) {
        const time = from.format('HH:mm')
        times.push(time)
        from = from.add(stepMinute, 'minutes')
      }
    }
  }
  return times
})

const hasFixedBeforeTo = computed(() => {
  return Boolean(fixedPeriod.value) && (fixedBeforeToTimes.value.length > 0)
})

const fromTimeItems = computed((): TimetableTime[] => {
  if (props.date.periods.length === 1 && props.date.periods[0].stepMinute === 0) {
    // Просто отображение времени без выбора
    return [{
      type: 'day',
      minutes: dayjs.duration(objTime(fromTime.value)).asMinutes(),
      value: fromTime.value,
      title: fromTime.value
    }]
  }

  const orderFromTime = orderDates.value[directionName.value]?.from
  const lastInterval = timeIntervals.value?.[timeIntervals.value.length - 1]?.value
  const endTimeList = [dayjs(`2000T${lastInterval}`).subtract(hourInterval.value, 'hour')]

  if (timeLimit.value?.ngtStart && !fixedTime.value) {
    endTimeList.push(dayjs(timeLimit.value?.ngtStart))
  }

  const endTime = dayjs.min(endTimeList).format('HH:mm')
  const endTimeMinutes = dayjs.duration(objTime(endTime)).asMinutes()
  let result: TimetableTime[] = []

  if (hasFixedPeriod.value && fixedTime.value) {
    const fixedFrom = dayjs(fixedPeriod.value?.from).format('HH:mm')
    result = deepCopyObject(timeIntervals.value.filter(ti => ti.minutes >= dayjs.duration(objTime(fixedFrom)).asMinutes()))
  } else {
    result = deepCopyObject(timeIntervals.value.filter(ti => ti.minutes <= endTimeMinutes))
  }

  if (orderFromTime && !result.find(item => item.value === orderFromTime)) {
    result.unshift({
      type: 'order',
      minutes: dayjs.duration(objTime(orderFromTime)).asMinutes(),
      value: orderFromTime,
      title: orderFromTime
    })
  }

  return result
})

const toTimeItems = computed((): TimetableTime[] => {
  if (props.date.periods.length === 1 && props.date.periods[0].stepMinute === 0) {
    return [{
      type: 'day',
      minutes: dayjs.duration(objTime(toTime.value)).asMinutes(),
      value: toTime.value,
      title: toTime.value
    }]
  }

  const firstTime = hasFixedBeforeTo.value && !fixedTime.value
    ? fixedPeriodFrom.value
    : fromTimeWithHourInterval.value
  let list = deepCopyObject(timeIntervals.value)
  const orderToTime = orderDates.value[directionName.value]?.to
  const addOrderTime = orderToTime && !list.find(item => item.value === orderToTime)

  list = list.filter(ti => ti.minutes >= dayjs.duration(objTime(firstTime)).asMinutes() && dayjs.duration(objTime(fromTime.value)).asMinutes())

  list.forEach((l) => {
    if (l.type !== 'morning') {
      l.type = 'day'
      l.url = ''
    }
    if (hasFixedBeforeTo.value && fixedBeforeToTimes.value.includes(l.value) && !fixedTime.value) {
      l.url = '/svg/icon/fixed.svg'
    }
  })
  markEvening(list)

  if (addOrderTime) {
    list.unshift({
      type: 'order',
      minutes: dayjs.duration(objTime(orderToTime)).asMinutes(),
      value: orderToTime,
      title: orderToTime
    })
  }

  const time = list.find(l => l.value === toTime.value)
  if (!time && list.length) {
    setTimeTo(list[0].value)
  }

  return list
})

const noteFromPeriod = computed((): string => {
  const period = props.date.periods.find(d => d.type === selectedTime.value?.type)
  return props.date.note || period?.note || ''
})

const isNewOrder = computed(() => {
  return !useRoute().params.guid
})

const isConfirmableTimeLocation = computed(() => {
  if (!props.confirmableTime) {
    return false
  }
  return props.directionTo ? isConfirmableTimeDestination.value : isConfirmableTimeDispatch.value
})

const isFromTimeEmpty = computed(() => {
  return isNewOrder.value && isConfirmableTimeLocation.value && !userChangedFromTime.value
})

const isToTimeEmpty = computed(() => {
  return isNewOrder.value && isConfirmableTimeLocation.value && !userChangedToTime.value
})

// если у нас пустое время fromTime скроллим выпадающий список до оптимального времени
const emptyFromScrollItem = computed(() => {
  return isFromTimeEmpty.value ? dayjs(props.date.optimalTime.from).format('HH:mm') : undefined
})

// если у нас пустое время toTime скроллим выпадающий список до интервального времени (fromTime + intervalHour)
const emptyToScrollItem = computed(() => {
  return isToTimeEmpty.value ? fromTimeWithHourInterval.value : undefined
})

const fromTimeWithHourInterval = computed(() => {
  if (!fromTime.value) {
    return ''
  }

  const fromTimeList = [dayjs(`2000T${fromTime.value}`).add(hourInterval.value, 'hour')]

  if (timeLimit.value?.nltEnd && !fixedTime.value) {
    fromTimeList.push(dayjs(timeLimit.value?.nltEnd))
  }

  return dayjs.max(fromTimeList).format('HH:mm')
})

watch(
  () => props.date,
  async (date: TimetableDate, oldDate?: TimetableDate): Promise<void> => {
    timeIntervals.value = []
    findPeriodDates(date.periods)
    const period = date.periods.find(p => p.type === 'fixed')
    hasFixedPeriod.value = period !== undefined

    let from = fromTimeItems.value.find(i => i.value === calendar.value.from)?.value || ''
    let to = toTimeItems.value.find(i => i.value === calendar.value.to)?.value || ''

    if (oldDate && date.date !== oldDate.date) {
      if (/*props.directionTo && */fixedTime.value) {
        if (hasFixedPeriod.value && period) {
          updateOptimalTime({
            from: dayjs(period.optimalTime?.from || period.from).format('HH:mm'),
            to: dayjs(period.optimalTime?.to || period.defaultFixTimeEnd || period.optimalTimeEnd).format('HH:mm')
          })
        } else {
          onChangeFixedTime(false)
        }
      } else {
        updateOptimalTime({
          from: formatDate(date.optimalTime.from, 'HH:mm'),
          to: formatDate(date.optimalTime.to, 'HH:mm')
        })
      }
    } else if (!period && date.periods.length) {
      updateOptimalTime({
        from: from || formatDate(date.optimalTime.from, 'HH:mm'),
        to: to || formatDate(date.optimalTime.to, 'HH:mm')
      })
    } else if (period) {
      if (fixedTime.value) {
        if ((!from || !to) && (calendar.value.from || calendar.value.to) && !isFromTimeEmpty.value && !isToTimeEmpty.value) {
          $showToast({
            variant: 'info',
            title: 'Внимание',
            text: 'Указанное вами время недоступно. Выставлено время по умолчанию'
          })
        }
        updateOptimalTime({
          from: from || formatDate(period?.optimalTime?.from || period.from, 'HH:mm'),
          to: to || formatDate(period?.optimalTime?.to || period.defaultFixTimeEnd || period.optimalTimeEnd, 'HH:mm')
        })
      } else {
        if (from === '00:00') {
          from = formatDate(date.optimalTime.from, 'HH:mm')
        }
        if (to === '00:00') {
          to = formatDate(date.optimalTime.to, 'HH:mm')
        }
        updateOptimalTime({
          from: from || formatDate(date.optimalTime.from, 'HH:mm'),
          to: to || formatDate(date.optimalTime.to, 'HH:mm')
        })
      }
    }
  },
  {deep: true, immediate: true}
)

watch(
  () => isConfirmableTimeLocation.value,
  () => {
    nextTick(() => {
      userChangedFromTime.value = false
      userChangedToTime.value = false
    })
  }
)

watch(
  () => fromTime.value,
  () => {
    nextTick(() => {
      fromTimeSelect.value?.checkError()
    })
  }
)

watch(
  () => toTime.value,
  () => {
    nextTick(() => {
      toTimeSelect.value?.checkError()
    })
  }
)

function setTimeFrom(value: string) {
  orderForm.value.directions[directionName.value].calendar.from = isFromTimeEmpty.value
    ? ''
    : value
}

function setTimeTo(value: string) {
  orderForm.value.directions[directionName.value].calendar.to = isToTimeEmpty.value
    ? ''
    : value
}

async function onChangeTimeFrom(time: TimetableTime): Promise<void> {
  const timeTo = orderForm.value.directions[directionName.value].calendar.to
  const diff = dayjs.duration(objTime(timeTo)).asHours() - dayjs.duration(objTime(time.value)).asHours()

  setUserChangedTime()
  selectedTime.value = time
  await setTimeFrom(time.value)
  if (diff <= hourInterval.value) {
    await setTimeTo(fromTimeWithHourInterval.value)
  }
  emit('select-time', time.value, 'from')
}

async function onChangeTimeTo(time: TimetableTime): Promise<void> {
  if (hasFixedBeforeTo.value && fixedBeforeToTimes.value.includes(time.value) && !fixedTime.value) {
    // Переход на фиксированное время
    const stepHour = fixedPeriod.value?.intervalHour || 0
    const from = orderForm.value.directions[directionName.value].calendar.from
    const diff = dayjs.duration(objTime(time.value)).hours() - dayjs.duration(objTime(from)).hours()
    let newFrom = dayjs(`2000T${time.value}`).subtract(stepHour, 'hour').format('HH:mm')

    if (dayjs.duration(objTime(newFrom)).hours() <= dayjs.duration(objTime(fixedPeriodFrom.value)).hours()) {
      newFrom = fixedPeriodFrom.value
    } else if (diff > stepHour) {
      newFrom = from
    }

    setUserChangedTime(false)
    orderForm.value.directions[directionName.value].calendar.fixedTime = true
    const times = {
      from: newFrom,
      to: time.value
    }
    await calculateTimeIntervals(props.date.periods)
    await updateTypes(props.date.periods)
    updateOptimalTime(times)
  } else {
    setUserChangedTime(false)
    await setTimeTo(time.value)
  }
  emit('select-time', time.value, 'to')
}

async function onChangeFixedTime(value: boolean): Promise<void> {
  setUserChangedTime()
  setUserChangedTime(false)

  const period = props.date.periods.find(p => p.type === 'fixed')
  orderForm.value.directions[directionName.value].calendar.fixedTime = value
  await nextTick()
  let time = {
    from: dayjs(props.date.optimalTime.from).format('HH:mm'),
    to: dayjs(props.date.optimalTime.to).format('HH:mm')
  }

  if (fixedTime.value) {
    time = {
      from: dayjs(period?.optimalTime?.from || period?.from).format('HH:mm'),
      to: dayjs(period?.optimalTime?.to || period?.defaultFixTimeEnd || period?.optimalTimeEnd).format('HH:mm')
    }
  }

  await calculateTimeIntervals(props.date.periods)
  await updateTypes(props.date.periods)
  updateOptimalTime(time)
  emit('select-time', time)
}

function setUserChangedTime(onFrom = true) {
  if (onFrom && isFromTimeEmpty.value) {
    userChangedFromTime.value = true
  }
  if (!onFrom && isToTimeEmpty.value) {
    userChangedToTime.value = true
  }
}

function onChangeAwaitingTime(): void {
  $el.value?.dispatchEvent(new Event('input', {bubbles: true, cancelable: true}))
  emit('select-time')
}

function updateOptimalTime(time: { from: string, to: string }): void {
  setTimeFrom(time.from)
  setTimeTo(time.to)
}

function findPeriodDates(periods: TimetableDatePeriod[]): void {
  calculateTimeIntervals(periods)
  updateTypes(periods)
}

function calculateTimeIntervals(periods: TimetableDatePeriod[]): void {
  let firstTime: Dayjs | null = null
  let lastTime: Dayjs | null = null
  let stepMinutes = 30
  timeIntervals.value = []

  if (!props.date.periods.find(d => d.type === 'fixed')) {
    periods.forEach((period) => {
      stepMinutes = period.stepMinute
      const from = dayjs(period.from)
      const to = dayjs(period.to)
      hourInterval.value = period.intervalHour
      if (!firstTime) {
        firstTime = from
      } else if (firstTime.isSameOrAfter(from)) {
        firstTime = from
      }

      if (!lastTime) {
        lastTime = to
      } else if (firstTime.isSameOrBefore(to)) {
        lastTime = to
      }
    })
  } else {
    const tmpFixedPeriod = periods.find(p => p.type === 'fixed') as TimetableDatePeriod
    periods.forEach((period) => {
      stepMinutes = period.stepMinute
      const from = dayjs(period.from)
      const to = dayjs(period.to)

      if (period === tmpFixedPeriod && fixedTime.value) {
        hourInterval.value = tmpFixedPeriod?.intervalHour
      } else if (period !== tmpFixedPeriod) {
        hourInterval.value = period.intervalHour
      }
      if (!firstTime) {
        firstTime = from
      } else if (firstTime.isSameOrAfter(from)) {
        firstTime = from
      }

      if (!lastTime) {
        lastTime = to
      } else if (firstTime.isSameOrBefore(to)) {
        lastTime = to
      }
    })
  }
  let from = dayjs(firstTime)
  const to = dayjs(lastTime)

  if (stepMinutes) {
    while (from.isSameOrBefore(to)) {
      const time = from.format('HH:mm')
      timeIntervals.value.push({
        title: time,
        value: time,
        minutes: dayjs.duration(objTime(time)).asMinutes(),
        type: '',
        url: ''
      })
      from = from.add(stepMinutes, 'minutes')
    }
  }
}

function updateTypes(periods: TimetableDatePeriod[]): void {
  periods.forEach((period) => {
    if ((period.type !== 'fixed' && !fixedTime.value) || (period.type === 'fixed' && fixedTime.value)) {
      let from = dayjs(period.from)
      const to = dayjs(period.to)
      if (period.type === 'evening') {
        from.subtract(period.intervalHour, 'hour').add(period.stepMinute, 'minute')
        to.subtract(period.intervalHour, 'hour')
      }

      if (period.stepMinute) {
        while (from.isSameOrBefore(to)) {
          const time = timeIntervals.value.find(p => p.value === from.format('HH:mm'))
          if (time) {
            time.type = period.type
            time.url = !['day', 'fixed'].includes(period.type) ? `/svg/${period.type}.svg` : ''
            const takeConditions = addSecondTypeWithConditions(time)
            time.secondType = takeConditions.type
            time.note = takeConditions.note
          }
          from = from.add(period.stepMinute, 'minute')
        }
      }
    }
  })
}

function addSecondTypeWithConditions(time: TimetableTime) {
  if (!props.date.conditions) {
    return {type: '', note: ''}
  }

  try {
    const findCondition = props.date.conditions.find((condition) => {
      const startCondition = dayjs(condition.start)
      const endCondition = dayjs(condition.end)

      const splitTime = time.value.split(':')
      const curTime = dayjs(condition.start).set('hour', +splitTime[0]).set('minute', +splitTime[1])
      return curTime.isBetween(startCondition, endCondition, null, '[]')
    })

    return {type: findCondition?.type || '', note: findCondition?.note || ''}
  } catch (e) {
    console.error('[addSecondTypeWithConditions]', e)
  }

  return {type: '', note: ''}
}

function markEvening(list: TimetableTime[]): void {
  const period = props.date.periods.find(p => p.type === 'evening')
  if (period) {
    let from = dayjs(period.from).add(period.stepMinute, 'minutes')
    const to = dayjs(period.to)
    if (period.stepMinute) {
      while (from.isSameOrBefore(to)) {
        const interval = from.format('HH:mm')
        const time = list.find(l => l.value === interval)
        if (time) {
          time.type = period.type
          time.url = `/svg/${period.type}.svg`
        }
        from = from.add(period.stepMinute, 'minute')
      }
    }
  }
}


// v v v v v v v v v уведомление "на изменение стоимости услуг в связи с выбором срочного интервала доставки"
// одноразовый флаг при переключении на timeTable quickly и изменение цены
const needToastQuickly = ref(false)

const getServicesCityPrice = computed(() => {
  const servicesPrice = orderForm.value.services?.find((val) => val.title === 'Перевозка между городами')
  if (!servicesPrice) {
    return 0
  }

  return servicesPrice.base
})

watch(() => getServicesCityPrice.value, (val) => {
  if (!val) {
    return
  }

  if (needToastQuickly.value) {
    $showToast({
      title: 'Внимание',
      text: 'Обращаем ваше внимание на изменение стоимости услуг в связи с выбором срочного интервала доставки',
      variant: 'info'
    })

    needToastQuickly.value = false
  }
})

watch(() => selectedTime.value.type, (val) => {
  if (val === 'quickly') {
    needToastQuickly.value = true
    return
  }

  needToastQuickly.value = false
})
// ^ ^ ^ ^ ^ ^ ^
</script>

<style lang="scss" scoped>
.vz-direction-calendar-time-fixed-time--label {
  @media screen and (max-width: $pa-mdm) {
    br {
      display: none;
    }
  }
}

.vz-direction-calendar-time-disabled {
  color: $inactive !important;

  .text-subtitle, .text-body {
    color: $inactive !important;
  }
}
</style>
